import { dashboard, payments } from '../api';
import { createStatCard } from '../components/card';
import { createPageHeader } from '../components/form';
import { navigate } from '../router';
import type { DashboardData, ChartData, ComplianceStatus, AttentionItem, PaymentTimeline } from '../types';

declare const Chart: any;

function fmt(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

export async function dashboardPage(): Promise<HTMLElement> {
  const page = document.createElement('div');
  page.appendChild(createPageHeader('Dashboard'));

  let data: DashboardData;
  let timelineData: PaymentTimeline[] = [];
  try {
    [data, timelineData] = await Promise.all([
      dashboard.get(),
      payments.timeline(2).catch(() => [] as PaymentTimeline[]),
    ]);
  } catch {
    page.innerHTML += '<div class="text-red-500 p-4">Failed to load dashboard data.</div>';
    return page;
  }

  // Alert banner — shown when there are overdue rents or open maintenance
  const overdueRent = data.overdue_rent ?? [];
  const openMaintCount = data.maintenance.open_count;
  if (overdueRent.length > 0 || openMaintCount > 0) {
    const banner = document.createElement('div');
    banner.className = 'mb-6 flex flex-wrap items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm';
    const parts: string[] = [];
    if (overdueRent.length > 0) {
      const total = overdueRent.reduce((s, r) => s + r.shortfall, 0);
      parts.push(`<a href="#/financials" class="font-semibold text-amber-800 hover:underline">⚠ ${overdueRent.length} propert${overdueRent.length === 1 ? 'y has' : 'ies have'} overdue rent (${fmt(total)} shortfall this month)</a>`);
    }
    if (openMaintCount > 0) {
      parts.push(`<a href="#/maintenance" class="font-semibold text-amber-800 hover:underline">${openMaintCount} open maintenance request${openMaintCount === 1 ? '' : 's'}</a>`);
    }
    banner.innerHTML = parts.join('<span class="text-amber-400 mx-1">·</span>');
    page.appendChild(banner);
  }

  // Portfolio stats
  const statsGrid = document.createElement('div');
  statsGrid.className = 'grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8';
  statsGrid.appendChild(createStatCard('Properties', data.portfolio.property_count));
  statsGrid.appendChild(createStatCard('Portfolio Value', fmt(data.portfolio.total_estimated_value)));
  statsGrid.appendChild(createStatCard('Weekly Rent', fmt(data.portfolio.total_weekly_rent)));
  statsGrid.appendChild(createStatCard('Annual Rent', fmt(data.portfolio.total_annual_rent)));
  page.appendChild(statsGrid);

  // Main layout: content + sidebar
  const mainLayout = document.createElement('div');
  mainLayout.className = 'grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8';

  const mainContent = document.createElement('div');
  mainContent.className = 'lg:col-span-3 space-y-8';

  // Financial YTD
  const finSection = document.createElement('div');
  finSection.innerHTML = `<h2 class="text-lg font-semibold text-gray-900 mb-4">Financial Year to Date <span class="text-sm font-normal text-gray-400">from ${data.financials_ytd.fy_start}</span></h2>`;
  const finGrid = document.createElement('div');
  finGrid.className = 'grid grid-cols-2 lg:grid-cols-5 gap-4';
  finGrid.appendChild(createStatCard('Rent Received', fmt(data.financials_ytd.total_rent_received), undefined, 'text-green-600'));
  finGrid.appendChild(createStatCard('Agent Fees', fmt(data.financials_ytd.total_agent_fees), undefined, 'text-red-500'));
  finGrid.appendChild(createStatCard('Maintenance', fmt(data.financials_ytd.total_maintenance_costs), undefined, 'text-orange-500'));
  finGrid.appendChild(createStatCard('Fixed Costs', fmt(data.financials_ytd.total_fixed_costs ?? 0), undefined, 'text-purple-500'));
  finGrid.appendChild(createStatCard('Net Income', fmt(data.financials_ytd.net_income), undefined, data.financials_ytd.net_income >= 0 ? 'text-green-600' : 'text-red-500'));
  finSection.appendChild(finGrid);
  mainContent.appendChild(finSection);

  // Rent Tracker table
  if (timelineData.length > 0) {
    const rentTracker = document.createElement('div');
    rentTracker.className = 'space-y-2';
    rentTracker.innerHTML = '<h2 class="text-lg font-semibold text-gray-900 mb-3">Rent Tracker <span class="text-sm font-normal text-gray-400">— current month</span></h2>';

    const tbl = document.createElement('div');
    tbl.className = 'card overflow-x-auto p-0';
    let html = `<table class="data-table text-sm"><thead><tr>
      <th>Property</th>
      <th class="text-right">Expected/mo</th>
      <th class="text-right">Received</th>
      <th>Paid To</th>
      <th>Status</th>
      <th></th>
    </tr></thead><tbody>`;

    for (const tl of timelineData) {
      const last = tl.months[tl.months.length - 1];
      if (!last) continue;
      const colorMap: Record<string, string> = { green: 'badge-green', amber: 'badge-yellow', red: 'badge-red', gray: 'badge-neutral' };
      const labelMap: Record<string, string> = { green: 'Paid', amber: 'Partial', red: 'Overdue', gray: '—' };
      const badge = colorMap[last.color] ?? 'badge-neutral';
      const label = labelMap[last.color] ?? 'No Data';
      const amtBadge = colorMap[last.amount_color] ?? 'badge-neutral';
      const paidTo = tl.paid_to ? new Date(tl.paid_to + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
      const pct = last.expected > 0 ? Math.round((last.actual / last.expected) * 100) : 0;
      html += `<tr>
        <td><a href="#/properties/${tl.property_id}" class="text-primary-600 hover:underline font-medium">${tl.address}</a></td>
        <td class="text-right">${fmt(last.expected)}</td>
        <td class="text-right font-medium"><span class="badge ${amtBadge}">${fmt(last.actual)} (${pct}%)</span></td>
        <td class="text-center text-xs">${paidTo}</td>
        <td><span class="badge ${badge}">${label}</span></td>
        <td class="text-right"><a href="#/financials" class="text-xs text-gray-400 hover:text-primary-600">History →</a></td>
      </tr>`;
    }

    html += '</tbody></table>';
    tbl.innerHTML = html;
    rentTracker.appendChild(tbl);
    mainContent.appendChild(rentTracker);
  }

  // Chart section
  const chartSection = document.createElement('div');
  chartSection.innerHTML = `<h2 class="text-lg font-semibold text-gray-900 mb-4">Monthly Income &amp; Expenses</h2>`;

  let chartData: ChartData | null = null;
  let chartMonthsBack = 6;
  let chartMonthsFwd = 6;
  let chartViewMode: 'chart' | 'table' = 'chart';

  const chartControls = document.createElement('div');
  chartControls.className = 'flex flex-wrap items-center gap-3 mb-4';
  chartControls.innerHTML = `
    <label class="text-sm text-gray-500">Period:</label>
    <select id="chart-period" class="input-field w-auto py-1 text-sm">
      <option value="1,0">Last Month</option>
      <option value="3,0">Last Quarter</option>
      <option value="6,6" selected>Last 6 Months + Projection</option>
      <option value="12,0">Last Year</option>
      <option value="0,3">Next Quarter</option>
      <option value="12,12">Full Year + Projection</option>
    </select>
    <div class="flex rounded-lg border border-gray-200 overflow-hidden ml-auto">
      <button class="px-3 py-1 text-sm font-medium chart-view-btn bg-primary-50 text-primary-700" data-view="chart">Chart</button>
      <button class="px-3 py-1 text-sm font-medium chart-view-btn text-gray-500 hover:bg-gray-50" data-view="table">Table</button>
    </div>
  `;
  chartSection.appendChild(chartControls);

  const chartContent = document.createElement('div');
  chartSection.appendChild(chartContent);
  mainContent.appendChild(chartSection);

  async function loadChart() {
    chartContent.innerHTML = '<div class="text-center py-8 text-gray-400">Loading chart...</div>';
    try {
      chartData = await dashboard.chart(chartMonthsBack);
    } catch {
      chartContent.innerHTML = '<div class="text-red-500 p-4">Failed to load chart data.</div>';
      return;
    }
    renderChartView();
  }

  function renderChartView() {
    if (!chartData) return;
    chartContent.innerHTML = '';
    if (chartViewMode === 'chart') {
      chartContent.appendChild(renderInteractiveBarChart(chartData, chartMonthsFwd));
    } else {
      chartContent.appendChild(renderChartTable(chartData, chartMonthsFwd));
    }
  }

  chartControls.querySelector('#chart-period')!.addEventListener('change', (e) => {
    const [back, fwd] = (e.target as HTMLSelectElement).value.split(',').map(Number);
    chartMonthsBack = back;
    chartMonthsFwd = fwd;
    loadChart();
  });
  chartControls.querySelectorAll('.chart-view-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      chartControls.querySelectorAll('.chart-view-btn').forEach(b => {
        b.classList.remove('bg-primary-50', 'text-primary-700');
        b.classList.add('text-gray-500');
      });
      btn.classList.add('bg-primary-50', 'text-primary-700');
      btn.classList.remove('text-gray-500');
      chartViewMode = btn.getAttribute('data-view') as 'chart' | 'table';
      renderChartView();
    });
  });

  await loadChart();

  // Three-column layout for compliance, maintenance, reminders
  const bottom = document.createElement('div');
  bottom.className = 'grid grid-cols-1 lg:grid-cols-3 gap-6';

  // Compliance
  if (data.compliance && data.compliance.length > 0) {
    const compCard = document.createElement('div');
    compCard.className = 'card';
    compCard.innerHTML = `
      <h3 class="font-semibold text-gray-900 mb-4">Compliance Status</h3>
      <div class="space-y-3">
        ${data.compliance.map((c: ComplianceStatus) => `
          <div class="p-3 rounded-lg bg-gray-50">
            <p class="text-sm font-medium text-gray-900 mb-2"><a href="#/properties/${c.property_id}" class="text-primary-600 hover:underline">${c.address}</a></p>
            <div class="flex gap-2 flex-wrap">
              <span class="badge badge-${statusColor(c.gas)}" title="Gas Safety">Gas: ${c.gas}</span>
              <span class="badge badge-${statusColor(c.electrical)}" title="Electrical Safety">Elec: ${c.electrical}</span>
              <span class="badge badge-${statusColor(c.smoke_alarm)}" title="Smoke Alarm">Smoke: ${c.smoke_alarm}</span>
            </div>
          </div>
        `).join('')}
      </div>
    `;
    bottom.appendChild(compCard);
  }

  // Maintenance Queue (tabbed)
  const maintCard = document.createElement('div');
  maintCard.className = 'card';
  const mTabs = [
    { key: 'needs_attention', label: 'Needs Attention', items: data.maintenance.needs_attention ?? [] },
    { key: 'in_progress', label: 'In Progress', items: data.maintenance.in_progress ?? [] },
    { key: 'completed_recent', label: 'Done (30d)', items: data.maintenance.completed_recent ?? [] },
  ];
  let activeMaintTab = 'needs_attention';
  const maintHeader = document.createElement('div');
  maintHeader.className = 'flex items-center gap-2 mb-3 flex-wrap';
  const tabButtons: HTMLButtonElement[] = mTabs.map(t => {
    const btn = document.createElement('button');
    btn.className = `text-xs px-2 py-1 rounded-full font-medium border maint-tab-btn ${t.key === activeMaintTab ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`;
    btn.textContent = `${t.label} (${t.items.length})`;
    btn.dataset.tab = t.key;
    return btn;
  });
  tabButtons.forEach(btn => maintHeader.appendChild(btn));
  maintCard.appendChild(maintHeader);
  const maintBody = document.createElement('div');
  maintBody.className = 'space-y-2';

  function renderMaintTab(key: string) {
    activeMaintTab = key;
    tabButtons.forEach(b => {
      const active = b.dataset.tab === key;
      b.className = `text-xs px-2 py-1 rounded-full font-medium border maint-tab-btn ${active ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`;
    });
    const tab = mTabs.find(t => t.key === key);
    if (!tab) return;
    maintBody.innerHTML = tab.items.length === 0
      ? '<p class="text-gray-400 text-sm py-2">None</p>'
      : tab.items.map(m => `
          <a href="#/properties/${m.property_id}/maintenance/${m.id}" class="flex items-start gap-3 p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors block">
            <span class="badge priority-${m.priority} text-xs">${m.priority}</span>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate">${m.title || m.description}</p>
              <p class="text-xs text-gray-400">${m.date_reported}</p>
            </div>
          </a>`).join('');
  }
  renderMaintTab(activeMaintTab);
  maintCard.appendChild(maintBody);
  maintCard.innerHTML += `<a href="#/maintenance" class="text-primary-600 text-xs hover:underline mt-3 inline-block">View all →</a>`;

  setTimeout(() => {
    maintCard.querySelectorAll('.maint-tab-btn').forEach(btn => {
      btn.addEventListener('click', () => renderMaintTab((btn as HTMLElement).dataset.tab!));
    });
  });
  bottom.appendChild(maintCard);

  // Reminders & Renewals
  const remCard = document.createElement('div');
  remCard.className = 'card';
  remCard.innerHTML = `
    <div class="flex justify-between items-center mb-4">
      <h3 class="font-semibold text-gray-900">Pending Reminders</h3>
      <span class="badge badge-yellow">${data.reminders.pending_count}</span>
    </div>
    <div class="space-y-3">
      ${data.reminders.items.length === 0 ? '<p class="text-gray-400 text-sm">All clear!</p>' :
      data.reminders.items.slice(0, 5).map(r => `
          <div class="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
            <div class="flex-1">
              <p class="text-sm font-medium text-gray-900">${r.type}</p>
              <p class="text-xs text-gray-400">Due: ${r.due_date}</p>
            </div>
            <span class="badge badge-${r.status === 'pending' ? 'yellow' : 'blue'}">${r.status}</span>
          </div>
        `).join('')}
    </div>
    ${data.reminders.pending_count > 0 ? '<a href="#/reminders" class="text-primary-600 text-sm hover:underline mt-3 inline-block">View all &rarr;</a>' : ''}
  `;

  // Upcoming lease + insurance renewals as countdown tiles
  const allRenewals = [
    ...data.upcoming_lease_renewals.map(l => ({ ...l, type: 'Lease', link: `#/properties/${l.property_id}` })),
    ...(data.upcoming_insurance_renewals ?? []).map(i => ({ ...i, days_until: i.days_until, type: 'Insurance', link: `#/properties/${i.property_id}` })),
  ].sort((a, b) => a.days_until - b.days_until);

  if (allRenewals.length > 0) {
    const urgencyClass = (d: number) =>
      d < 30 ? 'border-l-red-400 bg-red-50 text-red-700' :
      d < 60 ? 'border-l-amber-400 bg-amber-50 text-amber-700' :
      'border-l-gray-300 bg-gray-50 text-gray-700';

    remCard.innerHTML += `
      <div class="mt-4 pt-4 border-t border-gray-100">
        <h4 class="text-sm font-semibold text-gray-700 mb-3">Upcoming Renewals</h4>
        <div class="space-y-2">
          ${allRenewals.map(r => `
            <a href="${r.link}" class="flex items-center justify-between border-l-4 p-2 rounded ${urgencyClass(r.days_until)} hover:opacity-90 block">
              <div class="min-w-0">
                <span class="text-xs font-semibold uppercase tracking-wide">${r.type}</span>
                <p class="text-xs truncate">${r.address}</p>
              </div>
              <span class="text-lg font-bold ml-3 whitespace-nowrap">${r.days_until}d</span>
            </a>`).join('')}
        </div>
      </div>
    `;
  }

  bottom.appendChild(remCard);
  mainContent.appendChild(bottom);

  // Right sidebar — Needs Attention
  const sidebar = document.createElement('div');
  sidebar.className = 'space-y-4';

  if (data.attention_items && data.attention_items.length > 0) {
    const attCard = document.createElement('div');
    attCard.className = 'card';
    attCard.innerHTML = `<h3 class="font-semibold text-gray-900 mb-3 text-sm">Needs Attention (${data.attention_items.length})</h3>`;
    const attList = document.createElement('div');
    attList.className = 'space-y-2';
    data.attention_items.forEach((item: AttentionItem) => {
      const colorMap: Record<string, string> = {
        red: 'border-l-red-400 bg-red-50 text-red-700',
        amber: 'border-l-amber-400 bg-amber-50 text-amber-700',
        green: 'border-l-green-400 bg-green-50 text-green-700',
      };
      const cls = colorMap[item.color] || 'border-l-gray-400 bg-gray-50 text-gray-700';
      attList.innerHTML += `
        <div class="border-l-4 rounded p-2 ${cls}">
          <span class="text-xs font-medium">${item.message}</span>
          ${item.link ? `<a href="${item.link}" class="text-xs underline block mt-1">View</a>` : ''}
        </div>
      `;
    });
    attCard.appendChild(attList);
    sidebar.appendChild(attCard);
  } else {
    sidebar.innerHTML = '<div class="card text-center text-gray-400 text-sm py-6">All clear!</div>';
  }

  mainLayout.appendChild(mainContent);
  mainLayout.appendChild(sidebar);
  page.appendChild(mainLayout);

  return page;
}

function statusColor(status: string): string {
  if (status === 'valid') return 'green';
  if (status === 'expiring_soon') return 'yellow';
  if (status === 'expired') return 'red';
  return 'gray';
}

function renderInteractiveBarChart(chart: ChartData, monthsFwd: number): HTMLElement {
  const container = document.createElement('div');
  container.className = 'card';

  const curMonth = chart.current_month;
  const pastMonths = chart.months.filter(m => m <= curMonth);
  const futureMonths = chart.months.filter(m => m > curMonth).slice(0, monthsFwd);
  const displayMonths = [...pastMonths, ...futureMonths];

  const idx = (m: string) => chart.months.indexOf(m);
  const fixed = chart.fixed_expenses_monthly;
  const expRent = chart.expected_monthly_rent;
  const projFees = chart.projected_monthly_fees;

  const rentData = displayMonths.map(m => {
    const i = idx(m);
    return m > curMonth ? expRent : (i >= 0 ? chart.rent_income[i] : 0);
  });
  const feesData = displayMonths.map(m => {
    const i = idx(m);
    return m > curMonth ? projFees : (i >= 0 ? chart.agent_fees[i] : 0);
  });
  const maintData = displayMonths.map(m => {
    const i = idx(m);
    return m > curMonth ? 0 : (i >= 0 ? chart.maintenance_costs[i] : 0);
  });
  const fixedData = displayMonths.map(() => fixed);
  const netData = displayMonths.map((_, i) => rentData[i] - feesData[i] - maintData[i] - fixedData[i]);

  // Mark projected months with asterisk
  const labels = displayMonths.map(m => m > curMonth ? `${m.substring(5)}*` : m.substring(5));

  const wrapper = document.createElement('div');
  wrapper.style.position = 'relative';
  wrapper.style.height = '280px';
  const canvas = document.createElement('canvas');
  wrapper.appendChild(canvas);
  container.appendChild(wrapper);

  setTimeout(() => {
    new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Rent Income',
            data: rentData,
            backgroundColor: displayMonths.map(m => m > curMonth ? 'rgba(34,197,94,0.35)' : 'rgba(34,197,94,0.7)'),
            stack: 'income',
          },
          {
            label: 'Agent Fees',
            data: feesData,
            backgroundColor: displayMonths.map(m => m > curMonth ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.7)'),
            stack: 'expenses',
          },
          {
            label: 'Maintenance',
            data: maintData,
            backgroundColor: displayMonths.map(m => m > curMonth ? 'rgba(249,115,22,0.35)' : 'rgba(249,115,22,0.7)'),
            stack: 'expenses',
          },
          {
            label: 'Fixed Costs',
            data: fixedData,
            backgroundColor: displayMonths.map(m => m > curMonth ? 'rgba(168,85,247,0.35)' : 'rgba(168,85,247,0.7)'),
            stack: 'expenses',
          },
          {
            label: 'Net Income',
            data: netData,
            type: 'line',
            borderColor: 'rgba(59,130,246,1)',
            backgroundColor: 'rgba(59,130,246,0.1)',
            borderWidth: 2,
            pointRadius: 4,
            fill: false,
            tension: 0.3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          tooltip: {
            position: 'nearest',
            backgroundColor: '#21262d',
            titleColor: '#e6edf3',
            bodyColor: '#c9d1d9',
            borderColor: '#30363d',
            borderWidth: 1,
            callbacks: {
              label: (ctx: any) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
            },
          },
          legend: {
            labels: { color: '#8b949e' },
          },
        },
        scales: {
          x: {
            stacked: true,
            ticks: { color: '#8b949e' },
            grid: { color: '#21262d' },
          },
          y: {
            stacked: true,
            ticks: { callback: (v: number) => fmt(v), color: '#8b949e' },
            grid: { color: '#21262d' },
          },
        },
        onClick: (_evt: any, elements: any[]) => {
          if (elements.length > 0) {
            const idx = elements[0].index;
            const month = displayMonths[idx];
            navigate(`#/financials?month=${month}`);
          }
        },
      },
    });
  }, 0);

  // Expense breakdown chips
  if (chart.expense_breakdown && Object.keys(chart.expense_breakdown).length > 0) {
    const typeLabels: Record<string, string> = {
      council_rates: 'Council Rates',
      water_rates: 'Water Rates',
      land_tax: 'Land Tax',
      insurance: 'Insurance',
      strata: 'Strata',
      other: 'Other',
    };
    const chips = document.createElement('div');
    chips.className = 'mt-4 pt-3 border-t border-gray-100';
    let chipHtml = '<p class="text-xs text-gray-500 mb-2">Monthly fixed cost breakdown:</p><div class="flex flex-wrap gap-2">';
    for (const [type, amount] of Object.entries(chart.expense_breakdown).sort((a, b) => b[1] - a[1])) {
      const label = typeLabels[type] || type;
      chipHtml += `<span class="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-medium">${label}: ${fmt(amount)}</span>`;
    }
    chipHtml += `<span class="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-bold">Total: ${fmt(chart.fixed_expenses_monthly)}/mo</span>`;
    chipHtml += '</div>';
    chips.innerHTML = chipHtml;
    container.appendChild(chips);
  }

  return container;
}

function renderChartTable(chart: ChartData, monthsFwd: number): HTMLElement {
  const container = document.createElement('div');
  container.className = 'card overflow-x-auto';

  const curMonth = chart.current_month;
  const pastMonths = chart.months.filter(m => m <= curMonth);
  const futureMonths = chart.months.filter(m => m > curMonth).slice(0, monthsFwd);
  const displayMonths = [...pastMonths, ...futureMonths];

  const idx = (m: string) => chart.months.indexOf(m);
  const fixed = chart.fixed_expenses_monthly;
  const expRent = chart.expected_monthly_rent;
  const projFees = chart.projected_monthly_fees;

  let html = '<table class="data-table text-sm"><thead><tr>';
  html += '<th>Month</th><th class="text-right">Rent Income</th><th class="text-right">Agent Fees</th>';
  html += '<th class="text-right">Maintenance</th><th class="text-right">Fixed Costs</th>';
  html += '<th class="text-right">Total Expenses</th><th class="text-right">Net Income</th>';
  html += '</tr></thead><tbody>';

  let totalRent = 0, totalFees = 0, totalMaint = 0, totalFixed = 0;

  displayMonths.forEach(m => {
    const i = idx(m);
    const isFuture = m > curMonth;
    const rent = isFuture ? expRent : (i >= 0 ? chart.rent_income[i] : 0);
    const fees = isFuture ? projFees : (i >= 0 ? chart.agent_fees[i] : 0);
    const maint = isFuture ? 0 : (i >= 0 ? chart.maintenance_costs[i] : 0);
    const totalExp = fees + maint + fixed;
    const net = rent - totalExp;
    totalRent += rent; totalFees += fees; totalMaint += maint; totalFixed += fixed;

    const futureClass = isFuture ? 'italic text-gray-400' : '';
    const netColor = net >= 0 ? 'text-green-600' : 'text-red-500';
    const tag = isFuture ? ' *' : '';

    html += `<tr class="${futureClass}">
      <td class="font-medium">${m}${tag}</td>
      <td class="text-right">${fmt(rent)}</td>
      <td class="text-right">${fmt(fees)}</td>
      <td class="text-right">${fmt(maint)}</td>
      <td class="text-right">${fmt(fixed)}</td>
      <td class="text-right font-medium">${fmt(totalExp)}</td>
      <td class="text-right font-bold ${netColor}">${fmt(net)}</td>
    </tr>`;
  });

  // Totals row
  const totalNet = totalRent - totalFees - totalMaint - totalFixed;
  html += `<tr class="font-bold border-t-2 border-gray-300">
    <td>Total</td>
    <td class="text-right">${fmt(totalRent)}</td>
    <td class="text-right">${fmt(totalFees)}</td>
    <td class="text-right">${fmt(totalMaint)}</td>
    <td class="text-right">${fmt(totalFixed)}</td>
    <td class="text-right">${fmt(totalFees + totalMaint + totalFixed)}</td>
    <td class="text-right ${totalNet >= 0 ? 'text-green-600' : 'text-red-500'}">${fmt(totalNet)}</td>
  </tr>`;

  html += '</tbody></table>';
  html += '<p class="text-xs text-gray-400 mt-2">* projected based on current rents, average fees, and recorded fixed expenses</p>';

  container.innerHTML = html;
  return container;
}
